import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import {
  TDurationFilterType,
  TDurationGroupInfo,
  TDurationTypes,
  TRenderOptionType,
} from '../filter/model/ui-filter.types';
import { DateUtils } from '@monorepo/utils';

type TOption = {
  id: TDurationTypes;
  title: string;
  subtxt?: string;
  values?: TDurationFilterType;
  icon: string;
  activeIcon: string;
};

function _getDates(date1: number, date2?: number) {
  const values: number[] = [];
  // values.push(DateUtils.getDateStringFromDate(date1));
  // date2 && values.push(DateUtils.getDateStringFromDate(date2));
  values.push(date1);
  date2 && values.push(date2);
  return values;
}

function _getToday() {
  return {
    values: {
      type: 'TODAY' as TDurationTypes,
      start: Date.now(),
      end: Date.now(),
    },
    subtxt: moment(Date.now()).format('D MMM'),
  };
}

function _getPreviousDay() {
  const previous = new Date();
  previous.setDate(previous.getDate() - 1);

  return {
    values: {
      type: 'YESTERDAY' as TDurationTypes,
      start: previous.getTime(),
      end: previous.getTime(),
    },
    subtxt: moment(previous).format('D MMM'),
  };
}

function _getWeekly(date = new Date()) {
  const tmpDate = new Date(date.getTime());
  const today = new Date(date.getTime());
  const lastWeek = new Date(tmpDate.setDate(date.getDate() - 7));

  return {
    values: {
      type: 'WEEKLY' as TDurationTypes,
      start: lastWeek.getTime(),
      end: today.getTime(),
    },
    subtxt: `${moment(lastWeek).format('D MMM')} - ${moment(today).format('D MMM')}`,
  };
}

function _getMonthly(date = new Date()) {
  const tmpDate = new Date(date.getTime());
  const today = new Date(date.getTime());
  const lastMonth = new Date(tmpDate.setDate(date.getDate() - 30));

  return {
    values: {
      type: 'MONTHLY' as TDurationTypes,
      start: lastMonth.getTime(),
      end: today.getTime(),
    },
    subtxt: `${moment(lastMonth).format('D MMM')} - ${moment(today).format('D MMM')}`,
  };
}

const OPTION_LIST_CONF: TOption[] = [
  {
    id: 'TODAY',
    title: 'duration_picker.Today',
    icon: 'duration-picker__icons--today',
    activeIcon: 'duration-picker__icons--today-active',
    ..._getToday(),
  },
  {
    id: 'YESTERDAY',
    title: 'duration_picker.Yesterday',
    icon: 'duration-picker__icons--yesterday',
    activeIcon: 'duration-picker__icons--yesterday-active',
    ..._getPreviousDay(),
  },
  {
    id: 'WEEKLY',
    title: 'duration_picker.WeeklyView',
    icon: 'duration-picker__icons--weekly',
    activeIcon: 'duration-picker__icons--weekly-active',
    ..._getWeekly(),
  },
  {
    id: 'MONTHLY',
    title: 'duration_picker.MonthlyView',
    icon: 'duration-picker__icons--monthly',
    activeIcon: 'duration-picker__icons--monthly-active',
    ..._getMonthly(),
  },
  {
    id: 'CUSTOM',
    title: 'duration_picker.CustomDateRange',
    icon: 'duration-picker__icons--custom-date',
    activeIcon: 'duration-picker__icons--custom-date-active',
  },
];

@Component({
  selector: 'monorepo-duration-picker',
  templateUrl: './duration-picker.component.html',
  styleUrls: ['./duration-picker.component.scss'],
})
export class DurationPickerComponent implements OnInit {
  //props
  @Input() selectedDateFilter: TDurationFilterType | undefined;
  @Input() renderType: TRenderOptionType = 'MODAL';
  @Input() dateType: 'string' | 'number' | undefined = 'number';
  @Input() customLabelText: string = '';

  _itemsInfo: TDurationGroupInfo['items'] = [];
  @Input() set itemsInfo(value: TDurationGroupInfo['items']) {
    this._itemsInfo = [...value];
  }

  //component related data
  @Input() customFromDate: string | undefined;
  @Input() customToDate: string | undefined;
  @Input() showCustomDateError = false;

  @Output() onModelChange = new EventEmitter<TDurationGroupInfo['items']>();

  optionList = OPTION_LIST_CONF;

  currentSelectedDate: TDurationFilterType | undefined;

  constructor(private modalCtrl: ModalController, private translate: TranslateService) {}

  ngOnInit() {
    console.log('optionList before', this.optionList);
    if (this.dateType === 'string') {
      this.optionList = this.optionList.map((ol) => {
        if (ol.values) {
          const values = {
            ...ol.values,
            start: ol.values.start && DateUtils.getDateStringFromDate(Number(ol.values.start)),
            end: ol.values.end && DateUtils.getDateStringFromDate(Number(ol.values.end)),
          };
          return {
            ...ol,
            values,
          };
        }
        return ol;
      });
    }
    console.log('optionList after', this.optionList);
    if (this.selectedDateFilter) {
      switch (this.selectedDateFilter.type) {
        case 'CUSTOM':
          if (this.selectedDateFilter.start && this.selectedDateFilter.end) {
            this.customFromDate = moment(new Date(this.selectedDateFilter.start)).format('YYYY-MM-DD');
            this.customToDate = moment(new Date(this.selectedDateFilter.end)).format('YYYY-MM-DD');
          }
          break;
      }
      this.currentSelectedDate = this.selectedDateFilter;
    }
  }

  clearFilter() {
    this.modalCtrl.dismiss(undefined, 'ON_CLEAR');
  }

  applyFilter() {
    if (
      this.currentSelectedDate?.type === 'CUSTOM' &&
      (!this.currentSelectedDate.end || !this.currentSelectedDate.start)
    ) {
      this.showCustomDateError = true;
    } else {
      this.modalCtrl.dismiss(this.currentSelectedDate, 'ON_SELECTED');
    }
  }

  onCustomDatesChange() {
    this.showCustomDateError = false;
    if (this.customFromDate && this.customToDate) {
      if (this.customFromDate && this.customToDate) {
        const start = new Date(this.customFromDate).getTime();
        const end = new Date(this.customToDate).getTime();
        const dateFilter: TDurationFilterType = {
          type: 'CUSTOM',
          start: this.dateType === 'string' ? DateUtils.getDateStringFromDate(start) : start,
          end: this.dateType === 'string' ? DateUtils.getDateStringFromDate(end) : end,
        };
        this._itemsInfo = [];
        this._itemsInfo.push({
          value: dateFilter,
          isSelected: true,
        });
        console.log('this._itemsInfo CUSTOM', this._itemsInfo);
        this.onModelChange.emit(this._itemsInfo);

        this.currentSelectedDate = dateFilter;
        if (start >= end) {
          console.log('from date should be less than 2');
        }
      }
    }
  }

  changeSelected(selectedType: TDurationFilterType['type']) {
    this.showCustomDateError = false;
    if (selectedType !== 'CUSTOM') {
      (this.customFromDate = ''), (this.customToDate = '');
    }

    const selectedDates = this.optionList.find((_) => _.id === selectedType)?.values;
    if (selectedDates) {
      this._itemsInfo = [];
      this._itemsInfo.push({
        value: selectedDates,
        isSelected: true,
      });
      console.log('this._itemsInfo', this._itemsInfo);
      this.currentSelectedDate = selectedDates;
      this.onModelChange.emit(this._itemsInfo);
    }
  }

  dismissModal() {
    this.modalCtrl.dismiss();
  }
}
